import { FC, useEffect, useMemo, useRef } from 'react'
import { useRouter } from 'next/router'
import { useClearRefinements, useRefinementList } from 'react-instantsearch'

import { useApi } from '../../../services/useApi'
import useUrls from '../../../services/useUrls'
import BaseFilterPicker from './BaseFilterPicker'
import { isSeparateValueList } from '../../../lib/featureType'
import { createUrlParams } from '../../../lib/params'
import { isFilterAttribute, findFeatureValue } from '../../../lib/resources/attribute'
import { getPickerCode, getPickerValue, handlePickerChange } from './util'
import { not } from '../../../utils/util'
import { ProductAttribute } from '../../../utils/types/Product'
import { getCountryAndLocaleStrings } from '../../../utils/locales'
import { isDefined } from '../../../utils/types/misc'

import styles from '../../../styles/CatalogFilters.module.less'

interface TextFilterPickerProps {
  filter: ProductAttribute
  shouldCloseFilters: boolean
  isUnderFilterSection: boolean
}

const TextFilterPicker: FC<TextFilterPickerProps> = (props) => {
  const { filter, shouldCloseFilters } = props

  const { pushT } = useUrls()
  const { query, asPath, locale } = useRouter()
  const initializedRef = useRef(false)
  const { locale: lang } = getCountryAndLocaleStrings(locale)
  const { getResource } = useApi()
  const hasSeparateValueList = isSeparateValueList(filter.attribute || [])
  const valueListType = findFeatureValue(filter.attribute)?.['attribute.code'] ?? ''
  const typeName = filter['attribute.label']

  const filterCode = getPickerCode(filter)
  const pickerValue = useMemo(() => getPickerValue(query, filterCode), [query])

  const {
    refine: clear,
  } = useClearRefinements({
    includedAttributes: [typeName === 'Color' ? `color.${lang}` : `feature-types.${lang}.${typeName}`],
  })
  const {
    items,
    refine,
    searchForItems,
  } = useRefinementList({
    attribute: typeName === 'Color' ? `color.${lang}` : `feature-types.${lang}.${typeName}`,
    operator: 'or',
    limit: 100,
  })

  const shouldFetch = hasSeparateValueList && valueListType
  const { data: separateData } = getResource<ProductAttribute>(shouldFetch ? 'attribute' : null, createUrlParams({
    filter: {
      '==': {
        field: 'attribute.type',
        value: valueListType,
      },
    },
    page: { limit: '250' },
  }),
  {
    initialSize: 10,
  })

  const productAttributeList = hasSeparateValueList ? separateData : filter.attribute ?? []

  const pickerData = productAttributeList
    ?.filter(not(isFilterAttribute))
    .filter((attr) => (
      items.find((item) => item.label === attr['attribute.label'])
    ))
    .map((attr) => {
      const count = items.find((item) => item.label === attr['attribute.label'])?.count || 0
      return {
        label: (
          <>
            {attr['attribute.label']}
            <span className={`${styles.count} ${!count ? styles.disabled : ''}`}>
              {count}
            </span>
          </>
        ),
        value: attr.id,
      }
    })
    ?? []

  useEffect(() => {
    // Refine instantsearch filter items when loading page
    const refined = items.filter((item) => item.isRefined)
    if (!initializedRef.current && refined.length === 0 && pickerValue.length !== 0) {
      initializedRef.current = true
      productAttributeList
        .filter((attribute) => pickerValue.includes(attribute.id))
        .map((attribute) => attribute['attribute.label'])
        .filter(isDefined)
        .forEach((item) => {
          refine(item)
        })
    }
  }, [items, pickerValue, productAttributeList, refine])

  return (
    <BaseFilterPicker
      data={pickerData}
      filterCode={filterCode}
      onSearch={(value) => {
        searchForItems(value)
      }}
      placeholder={filter['attribute.label']}
      label={pickerValue.length ? filter['attribute.label'] : undefined}
      value={pickerValue}
      onChange={(value) => {
        handlePickerChange(pushT, query, asPath, filterCode)(value)
        initializedRef.current = true
        const isClear = value.length < pickerValue.length
        const isNew = (id: string) => !pickerValue.includes(id)
        if (isClear) {
          clear()
        }
        productAttributeList
          .filter((attribute) => (
            value.includes(attribute.id) && (isClear || isNew(attribute.id))))
          .map((attribute) => attribute['attribute.label'])
          .filter(isDefined)
          .forEach(refine)
      }}
      cleanable={false}
      onExit={() => searchForItems('')}
      shouldCloseFilters={shouldCloseFilters}
    />
  )
}

export default TextFilterPicker
